/* ─────────────────────────────────────────────────────────────
   gpc-hero-carousel.js
   Upload to: Assets/   (paired with sections/gpc-hero-carousel.liquid)

   Loaded GLOBALLY from snippets/head.liquid, NOT from the section:
     <script src="{{ 'gpc-hero-carousel.js' | asset_url }}" defer></script>

   A <script> placed inside a section on this storefront is never run,
   inline or external — it is not even fetched. Every gpc-* widget is
   loaded from head.liquid for that reason. Do not move this inline.

   What it does, beyond a normal slider:

   1. Videos never show black. Each slide always paints, bottom to top:
      solid colour → poster <img> → <video opacity:0>. The video only
      fades up once the browser fires `playing`, so a slow, stalled or
      blocked video leaves the poster on screen instead of a black box.
   2. Only the active slide and the one after it ever get a video src.
      Everything else stays as a bare <video> with no source, so a five
      slide hero does not pull five video files on first paint.
   3. Save-Data, 2g/slow-2g and prefers-reduced-motion fall back to the
      poster and skip video entirely.
   4. Per-slide timing. A slide can hold until its video ends instead of
      being cut off by the timer.
   5. Overlay reveals replay every time a slide comes back round.
   ───────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function saveData() {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return false;
    if (c.saveData) return true;
    return c.effectiveType === 'slow-2g' || c.effectiveType === '2g';
  }

  var CHEAP = REDUCE || saveData();
  var DESIGN = !!(window.Shopify && window.Shopify.designMode);

  function isMobile() {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  function init(root) {
    if (root.dataset.gpchReady === '1') return;
    root.dataset.gpchReady = '1';

    var stage = root.querySelector('[data-gpch-stage]');
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-gpch-slide]'));
    if (!stage || !slides.length) return;

    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-gpch-dot]'));
    var prevBtn = root.querySelector('[data-gpch-prev]');
    var nextBtn = root.querySelector('[data-gpch-next]');
    var playBtn = root.querySelector('[data-gpch-play]');
    var counterNow = root.querySelector('[data-gpch-counter-now]');

    var mode = root.getAttribute('data-transition') || 'fade';       // fade | slide
    var tdur = parseInt(root.getAttribute('data-tdur'), 10) || 700;
    var loop = root.getAttribute('data-loop') !== 'false';
    var autoplayOn = root.getAttribute('data-autoplay') === 'true' && slides.length > 1;
    var pauseHover = root.getAttribute('data-pause-hover') === 'true';

    if (REDUCE) tdur = 1;

    var index = -1;
    var timer = null;
    var paused = false;        // pointer / focus / offscreen / tab hidden
    var userPaused = false;    // the pause button
    var editorHold = false;    // a block is selected in the theme editor
    var busy = false;

    // Anything registered here is undone when the theme editor throws the
    // section away. The editor re-renders the whole section on EVERY setting
    // change, so without this each nudge of a slider left behind its own 1s
    // interval, its two observers and its document/window listeners, all
    // still firing against detached nodes. Fifty edits, fifty of each — that
    // is the editor getting slower the longer you work.
    var teardown = [];
    function keep(fn) { teardown.push(fn); }
    function bind(target, ev, fn, opt) {
      target.addEventListener(ev, fn, opt);
      keep(function () { target.removeEventListener(ev, fn, opt); });
    }

    /* ── media ─────────────────────────────────────────────── */

    // Attach a source to a slide's video, choosing the mobile file on small
    // screens. Called for the active slide and the next one only.
    function arm(slide) {
      var video = slide.querySelector('[data-gpch-video]');
      if (!video || CHEAP) return null;
      if (isMobile() && slide.getAttribute('data-mobile-video') === 'false') return null;

      if (!video.src) {
        var src = isMobile()
          ? (video.getAttribute('data-src-m') || video.getAttribute('data-src'))
          : (video.getAttribute('data-src') || video.getAttribute('data-src-m'));
        if (!src) return null;
        video.src = src;
        video.preload = 'auto';
        video.load();
      }
      return video;
    }

    // The poster stays visible underneath at all times. `is-ready` only fades
    // the video in over the top of it once frames are genuinely arriving.
    slides.forEach(function (slide) {
      var video = slide.querySelector('[data-gpch-video]');
      if (!video) return;
      var reveal = function () { video.classList.add('is-ready'); };
      video.addEventListener('playing', reveal);
      video.addEventListener('loadeddata', function () {
        // Some browsers never fire `playing` for a muted autoplay loop that
        // was already buffered. A decoded first frame is good enough.
        if (video.readyState >= 3) reveal();
      });
      video.addEventListener('error', function () {
        video.classList.remove('is-ready');
        slide.setAttribute('data-video-failed', 'true');
      });
      video.addEventListener('stalled', function () { video.classList.remove('is-ready'); });
    });

    function playSlideVideo(slide) {
      var video = arm(slide);
      if (!video) return;
      var p = video.play();
      // Autoplay rejection is normal (some iOS low-power states). The poster
      // is already on screen, so there is nothing to fall back to.
      if (p && p.catch) p.catch(function () {});
    }

    // Embeds inside a hidden slide measure themselves as zero. Trustpilot is
    // the one that matters here — its bootstrap scans the page once on load,
    // by which time every slide but the first is visibility:hidden. Re-running
    // its loader on first reveal is the supported way to fix that.
    function wakeEmbeds(slide) {
      var hosts = slide.querySelectorAll('[data-gpch-html]');
      Array.prototype.forEach.call(hosts, function (host) {
        if (host.dataset.gpchWoke === '1') return;
        host.dataset.gpchWoke = '1';
        if (window.Trustpilot && typeof window.Trustpilot.loadFromElement === 'function') {
          var boxes = host.querySelectorAll('.trustpilot-widget');
          Array.prototype.forEach.call(boxes, function (box) {
            try { window.Trustpilot.loadFromElement(box, true); } catch (e) {}
          });
        }
        // Anything else that needs a nudge can listen for this.
        host.dispatchEvent(new CustomEvent('gpch:visible', { bubbles: true }));
      });
    }

    /* ── countdown ─────────────────────────────────────────── */

    // The remaining seconds are worked out in Liquid against the SHOP's
    // clock and rendered into data-gpch-cd. All this does is tick it down,
    // so a visitor in another timezone still sees the right cutoff.
    var clocks = [];
    Array.prototype.forEach.call(root.querySelectorAll('[data-gpch-cd]'), function (el) {
      var left = parseInt(el.getAttribute('data-gpch-cd'), 10);
      if (!(left > 0)) return;
      clocks.push({
        el: el,
        end: (typeof performance !== 'undefined' ? performance.now() : 0) + left * 1000,
        expire: el.getAttribute('data-gpch-cd-expire'),
        h: el.querySelector('[data-gpch-cd-h]'),
        m: el.querySelector('[data-gpch-cd-m]'),
        s: el.querySelector('[data-gpch-cd-s]')
      });
    });

    function pad2(v) { return v < 10 ? '0' + v : String(v); }

    function tickClocks() {
      if (!clocks.length) return;
      var now = (typeof performance !== 'undefined' ? performance.now() : 0);
      for (var i = clocks.length - 1; i >= 0; i--) {
        var c = clocks[i];
        var left = Math.max(0, Math.round((c.end - now) / 1000));
        if (c.h) c.h.textContent = pad2(Math.floor(left / 3600));
        if (c.m) c.m.textContent = pad2(Math.floor(left / 60) % 60);
        if (c.s) c.s.textContent = pad2(left % 60);
        if (left <= 0) {
          if (c.expire) {
            c.el.innerHTML = '';
            var p = document.createElement('div');
            p.className = 'gpch-cd-lead';
            p.textContent = c.expire;
            c.el.appendChild(p);
          }
          clocks.splice(i, 1);
        }
      }
    }

    if (clocks.length) {
      tickClocks();
      var clockTimer = setInterval(tickClocks, 1000);
      keep(function () { clearInterval(clockTimer); });
    }

    /* ── stat counter ──────────────────────────────────────── */

    // Counts up the first time its slide is shown. The written value is the
    // source of truth — the separators and any prefix/suffix are preserved,
    // only the digits are animated, so "12,000+" stays "12,000+".
    function runCounters(slide) {
      var els = slide.querySelectorAll('[data-gpch-count]');
      Array.prototype.forEach.call(els, function (el) {
        if (el.dataset.gpchCounted === '1') return;
        el.dataset.gpchCounted = '1';
        var raw = el.getAttribute('data-gpch-count') || '';
        var target = parseFloat(raw.replace(/[^0-9.\-]/g, ''));
        if (!isFinite(target)) return;
        var dur = parseInt(el.getAttribute('data-gpch-count-ms'), 10) || 1600;
        if (REDUCE || dur < 100) return;            // leave the final value in place
        var full = el.textContent;
        var decimals = (raw.split('.')[1] || '').length;
        var grouped = raw.indexOf(',') !== -1;
        var start = null;

        function fmt(v) {
          var out = decimals ? v.toFixed(decimals) : String(Math.round(v));
          if (grouped) {
            var bits = out.split('.');
            bits[0] = bits[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            out = bits.join('.');
          }
          return full.replace(raw, out);
        }

        function step(ts) {
          if (start === null) start = ts;
          var t = Math.min(1, (ts - start) / dur);
          var eased = 1 - Math.pow(1 - t, 3);
          el.textContent = fmt(target * eased);
          if (t < 1) requestAnimationFrame(step);
          else el.textContent = full;
        }
        el.textContent = fmt(0);
        requestAnimationFrame(step);
      });
    }

    /* ── proportional scaling ──────────────────────────────── */

    // The overlay layer is drawn at a fixed design width and then zoomed to
    // fit. See the long note in gpc-hero-carousel.css for why. All this does
    // is publish the ratio; the CSS does the work.
    var fitOn = root.getAttribute('data-fit') !== 'false';
    var zoom = 1;

    function applyFit() {
      if (!fitOn) return;
      var w = stage.clientWidth, h = stage.clientHeight;
      if (!w || !h) return;
      var m = isMobile();
      var refW = parseFloat(root.getAttribute(m ? 'data-ref-m' : 'data-ref')) || (m ? 430 : 1200);
      var refH = parseFloat(root.getAttribute(m ? 'data-refh-m' : 'data-refh')) || (m ? 520 : 640);
      var cap = parseFloat(root.getAttribute('data-fit-max')) || 130;
      // The smaller ratio wins, so the whole design box fits on both axes —
      // width alone left the vertical space changing with the stage, which is
      // what made content crop off the bottom at some window sizes.
      zoom = Math.min(w / refW, h / refH, cap / 100);
      root.style.setProperty('--gpch-ref', refW);
      root.style.setProperty('--gpch-refh', refH);
      root.style.setProperty('--gpch-z', Math.round(zoom * 10000) / 10000);
    }

    if (fitOn) {
      root.classList.add('gpch--fit');
      applyFit();
      if ('ResizeObserver' in window) {
        var ro = new ResizeObserver(applyFit);
        ro.observe(stage);
        keep(function () { ro.disconnect(); });
      } else {
        bind(window, 'resize', applyFit);
      }
    }

    /* ── drag to position (theme editor only) ──────────────── */

    // Shopify gives a section no way to write back its own settings, so this
    // cannot save anything. What it can do is remove the guesswork: drag the
    // thing where you want it and read off the two numbers to type in.
    // The element is moved with a spare translate on top of its anchoring
    // one, so nothing about the real positioning is disturbed.
    if (root.classList.contains('gpch--drag')) {
      var hud = root.querySelector('.gpch-drag-hud');
      if (!hud) {
        hud = document.createElement('div');
        hud.className = 'gpch-drag-hud';
        (root.querySelector('.gpch-frame') || root).appendChild(hud);
      }

      var drag = null;

      var stageBox = function () { return stage.getBoundingClientRect(); };

      root.addEventListener('pointerdown', function (e) {
        var el = e.target.closest ? e.target.closest('[data-gpch-drag]') : null;
        if (!el || !root.contains(el)) return;
        var box = stageBox();
        if (!box.width || !box.height) return;
        // the mobile fields are the live ones below the breakpoint
        var m = isMobile();
        drag = {
          el: el, m: m, box: box,
          sx: e.clientX, sy: e.clientY,
          x0: parseFloat(el.getAttribute(m ? 'data-gpch-xm' : 'data-gpch-x')) || 0,
          y0: parseFloat(el.getAttribute(m ? 'data-gpch-ym' : 'data-gpch-y')) || 0
        };
        el.classList.add('is-dragging');
        // keep the carousel's own swipe handler out of it
        e.preventDefault();
        e.stopPropagation();
        try { root.setPointerCapture(e.pointerId); } catch (err) {}
      }, true);

      root.addEventListener('pointermove', function (e) {
        if (!drag) return;
        var dx = e.clientX - drag.sx;
        var dy = e.clientY - drag.sy;
        var z = zoom || 1;
        drag.el.style.setProperty('--dx', (dx / z) + 'px');
        drag.el.style.setProperty('--dy', (dy / z) + 'px');
        var nx = Math.round(drag.x0 + (dx / drag.box.width) * 100);
        var ny = Math.round(drag.y0 + (dy / drag.box.height) * 100);
        // the sliders stop at ±50, so say so rather than reporting a number
        // that cannot be entered
        var clamp = function (v) { return Math.max(-50, Math.min(50, v)); };
        var cx = clamp(nx), cy = clamp(ny);
        var over = (cx !== nx || cy !== ny) ? ' <i>(slider limit)</i>' : '';
        hud.innerHTML = drag.el.getAttribute('data-gpch-drag') +
          ' &nbsp;<i>' + (drag.m ? 'mobile' : 'desktop') + '</i>&nbsp; ' +
          'X <b>' + cx + '%</b> &nbsp; Y <b>' + cy + '%</b>' + over;
        hud.classList.add('is-on');
        e.preventDefault();
      }, true);

      var endDrag = function (e) {
        if (!drag) return;
        drag.el.classList.remove('is-dragging');
        try { root.releasePointerCapture(e.pointerId); } catch (err) {}
        drag = null;
      };
      root.addEventListener('pointerup', endDrag, true);
      root.addEventListener('pointercancel', endDrag, true);
      // a drag ends on a link often enough to be worth blocking
      root.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('[data-gpch-drag]')) e.preventDefault();
      }, true);
    }

    /* ── before / after wipe ───────────────────────────────── */

    // All the input does is move a CSS variable. Drag, touch and the arrow
    // keys are the range element's own behaviour, not ours.
    Array.prototype.forEach.call(root.querySelectorAll('[data-gpch-ba]'), function (ba) {
      var range = ba.querySelector('.gpch-ba-range');
      if (!range || ba.dataset.gpchBaReady === '1') return;
      ba.dataset.gpchBaReady = '1';
      var paint = function () { ba.style.setProperty('--pos', range.value + '%'); };
      range.addEventListener('input', paint);
      range.addEventListener('change', paint);
      paint();
    });

    function stopSlideVideo(slide) {
      var video = slide.querySelector('[data-gpch-video]');
      if (!video) return;
      try { video.pause(); } catch (e) {}
      video.classList.remove('is-ready');
      if (video.src) { try { video.currentTime = 0; } catch (e) {} }
    }

    /* ── timing ────────────────────────────────────────────── */

    function slideMs(slide) {
      return (parseFloat(slide.getAttribute('data-duration')) || 6) * 1000;
    }

    function holdsForVideo(slide) {
      if (slide.getAttribute('data-hold-video') !== 'true') return false;
      if (slide.getAttribute('data-video-failed') === 'true') return false;
      var video = slide.querySelector('[data-gpch-video]');
      return !!(video && video.src && !video.loop);
    }

    function clearTimer() {
      if (timer) { clearTimeout(timer); timer = null; }
    }

    function armTimer() {
      clearTimer();
      if (!autoplayOn || paused || userPaused || editorHold) return;
      var slide = slides[index];
      if (!slide) return;

      if (holdsForVideo(slide)) {
        // No timer — `ended` drives the advance. A safety net still fires in
        // case the video never reaches the end (network drop, tab throttle).
        var video = slide.querySelector('[data-gpch-video]');
        var guard = ((video && video.duration) ? video.duration * 1000 : slideMs(slide)) + 4000;
        timer = setTimeout(next, guard);
        return;
      }
      timer = setTimeout(next, slideMs(slide));
    }

    function paintProgress() {
      dots.forEach(function (d, i) {
        d.classList.toggle('is-active', i === index);
        d.setAttribute('aria-selected', i === index ? 'true' : 'false');
        d.tabIndex = i === index ? 0 : -1;
        var fill = d.querySelector('[data-gpch-fill]');
        if (!fill) return;
        fill.style.transition = 'none';
        fill.style.transform = 'scaleX(0)';
        if (i !== index || !autoplayOn || userPaused || editorHold) return;
        var slide = slides[index];
        var ms = holdsForVideo(slide) ? 0 : slideMs(slide);
        if (!ms) return;
        // reflow so the reset above is not merged with the animation below
        void fill.offsetWidth;
        fill.style.transition = 'transform ' + ms + 'ms linear';
        fill.style.transform = 'scaleX(1)';
      });
    }

    /* ── transition ────────────────────────────────────────── */

    function offset(dir, incoming) {
      if (mode !== 'slide') return '';
      if (incoming) return dir === 'prev' ? '-100%' : '100%';
      return dir === 'prev' ? '100%' : '-100%';
    }

    function goTo(i, dir) {
      if (busy) return;
      if (slides.length < 2) i = 0;
      if (i === index) return;

      if (i < 0) { if (!loop) return; i = slides.length - 1; }
      if (i > slides.length - 1) { if (!loop) return; i = 0; }

      dir = dir || (index === -1 || i > index ? 'next' : 'prev');
      var outgoing = slides[index] || null;
      var incoming = slides[i];
      // The lock exists to stop two transitions overlapping. The very first
      // paint has nothing to transition from, so locking there would just
      // swallow every click for the first transition-length of the page.
      busy = !!outgoing;

      if (outgoing) {
        outgoing.classList.remove('is-active', 'is-in');
        stopSlideVideo(outgoing);
        if (mode === 'slide') {
          outgoing.style.transition = 'transform ' + tdur + 'ms cubic-bezier(.4,0,.2,1)';
          outgoing.style.transform = 'translate3d(' + offset(dir, false) + ',0,0)';
          outgoing.classList.add('is-leaving');
        }
      }

      if (mode === 'slide') {
        incoming.style.transition = 'none';
        incoming.style.transform = 'translate3d(' + offset(dir, true) + ',0,0)';
        void incoming.offsetWidth;
        incoming.style.transition = 'transform ' + tdur + 'ms cubic-bezier(.4,0,.2,1)';
        incoming.style.transform = 'translate3d(0,0,0)';
      }

      incoming.classList.remove('is-leaving');
      incoming.classList.add('is-active');
      incoming.setAttribute('aria-hidden', 'false');
      if (outgoing) outgoing.setAttribute('aria-hidden', 'true');

      index = i;
      playSlideVideo(incoming);
      wakeEmbeds(incoming);
      runCounters(incoming);
      // next slide's video warms up in the background so it is decoded by the
      // time it is shown — the single biggest cause of a black first frame
      // Warming the next slide is what stops a black first frame, but in the
      // editor the section is rebuilt on every setting change, so this would
      // re-fetch a video each time you touch a slider. Live only.
      if (slides.length > 1 && !DESIGN) {
        var nx = slides[(i + 1) % slides.length];
        if (nx !== incoming) setTimeout(function () { arm(nx); }, 400);
      }

      // one frame later so the transition actually runs
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { incoming.classList.add('is-in'); });
      });

      if (counterNow) counterNow.textContent = i + 1;
      paintProgress();
      armTimer();

      if (outgoing) {
        setTimeout(function () {
          busy = false;
          if (mode === 'slide') outgoing.classList.remove('is-leaving');
        }, tdur + 20);
      }
    }

    function next() { goTo(index + 1, 'next'); }
    function prev() { goTo(index - 1, 'prev'); }

    /* ── pause plumbing ────────────────────────────────────── */

    function setPaused(v) {
      if (paused === v) return;
      paused = v;
      if (paused) {
        clearTimer();
        dots.forEach(function (d) {
          var fill = d.querySelector('[data-gpch-fill]');
          if (!fill) return;
          var w = fill.getBoundingClientRect().width;
          var parentW = fill.parentNode.getBoundingClientRect().width || 1;
          fill.style.transition = 'none';
          fill.style.transform = 'scaleX(' + (w / parentW) + ')';
        });
      } else {
        armTimer();
        paintProgress();
      }
    }

    if (pauseHover) {
      root.addEventListener('pointerenter', function () { setPaused(true); });
      root.addEventListener('pointerleave', function () { setPaused(false); });
    }
    root.addEventListener('focusin', function () { setPaused(true); });
    root.addEventListener('focusout', function () {
      if (!root.contains(document.activeElement)) setPaused(false);
    });
    bind(document, 'visibilitychange', function () {
      setPaused(document.hidden);
      if (!document.hidden && slides[index]) playSlideVideo(slides[index]);
    });

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            setPaused(false);
            if (slides[index]) playSlideVideo(slides[index]);
          } else {
            setPaused(true);
            if (slides[index]) {
              var v = slides[index].querySelector('[data-gpch-video]');
              if (v) { try { v.pause(); } catch (err) {} }
            }
          }
        });
      }, { threshold: 0.15 });
      io.observe(root);
      keep(function () { io.disconnect(); });
    }

    if (playBtn) {
      playBtn.addEventListener('click', function () {
        userPaused = !userPaused;
        root.classList.toggle('gpch--paused', userPaused);
        playBtn.setAttribute('aria-label', userPaused ? 'Play slideshow' : 'Pause slideshow');
        playBtn.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
        if (userPaused) clearTimer(); else { armTimer(); paintProgress(); }
      });
    }

    /* ── controls ──────────────────────────────────────────── */

    if (prevBtn) prevBtn.addEventListener('click', function (e) { e.preventDefault(); prev(); });
    if (nextBtn) nextBtn.addEventListener('click', function (e) { e.preventDefault(); next(); });
    dots.forEach(function (d, i) {
      d.addEventListener('click', function (e) {
        e.preventDefault();
        goTo(i, i > index ? 'next' : 'prev');
      });
    });

    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
    });

    // swipe — pointer events, ignores vertical scrolls and drags on links
    var sx = 0, sy = 0, tracking = false;
    stage.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      sx = e.clientX; sy = e.clientY; tracking = true;
    });
    stage.addEventListener('pointerup', function (e) {
      if (!tracking) return;
      tracking = false;
      var dx = e.clientX - sx, dy = e.clientY - sy;
      if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.4) return;
      if (dx < 0) next(); else prev();
    });
    stage.addEventListener('pointercancel', function () { tracking = false; });

    // a video that is meant to hold the slide advances it when it ends
    slides.forEach(function (slide) {
      var video = slide.querySelector('[data-gpch-video]');
      if (!video) return;
      video.addEventListener('ended', function () {
        if (slide !== slides[index]) return;
        if (!autoplayOn || paused || userPaused || editorHold) return;
        if (slide.getAttribute('data-hold-video') === 'true') next();
      });
    });

    bind(window, 'resize', function () {
      // viewport crossed the mobile breakpoint — the armed video may be the
      // wrong file now, so drop it and let the active slide re-arm
      var m = isMobile();
      if (root.dataset.gpchMobile === String(m)) return;
      root.dataset.gpchMobile = String(m);
      slides.forEach(function (s) {
        var v = s.querySelector('[data-gpch-video]');
        if (v && s !== slides[index]) { v.removeAttribute('src'); v.classList.remove('is-ready'); }
      });
      if (slides[index]) playSlideVideo(slides[index]);
    });

    // The theme editor hooks at the bottom of this file drive the carousel
    // through this, so selecting a block can bring its slide into view.
    root.gpchApi = {
      show: function (slide) {
        var i = slides.indexOf(slide);
        if (i < 0) return;
        editorHold = true;
        clearTimer();
        if (i !== index) { busy = false; goTo(i, i > index ? 'next' : 'prev'); }
      },
      release: function () {
        editorHold = false;
        armTimer();
        paintProgress();
      },
      destroy: function () {
        clearTimer();
        slides.forEach(stopSlideVideo);
        teardown.forEach(function (fn) { try { fn(); } catch (e) {} });
        teardown.length = 0;
      }
    };

    root.dataset.gpchMobile = String(isMobile());
    root.classList.add('gpch--ready');
    if (CHEAP) root.classList.add('gpch--cheap');
    goTo(0, 'next');
  }

  function boot(scope) {
    var roots = (scope || document).querySelectorAll('[data-gpch]');
    Array.prototype.forEach.call(roots, init);
  }

  // Loaded globally from head.liquid, so this runs on pages with no carousel
  // at all and exits immediately.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }
  window.addEventListener('load', function () { boot(); });

  // theme editor
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });

  // Selecting a Slide or an overlay in the sidebar brings that slide up and
  // holds it there, so the thing being edited stays on screen.
  function slideOf(node) {
    if (!node || !node.closest) return null;
    return node.closest('[data-gpch-slide]');
  }
  document.addEventListener('shopify:block:select', function (e) {
    var slide = slideOf(e.target);
    if (!slide) return;
    var root = slide.closest('[data-gpch]');
    if (root && root.gpchApi) root.gpchApi.show(slide);
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var slide = slideOf(e.target);
    if (!slide) return;
    var root = slide.closest('[data-gpch]');
    if (root && root.gpchApi) root.gpchApi.release();
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var r = e.target.matches && e.target.matches('[data-gpch]')
      ? e.target
      : e.target.querySelector('[data-gpch]');
    if (!r) return;
    if (r.gpchApi && r.gpchApi.destroy) r.gpchApi.destroy();
    r.dataset.gpchReady = '';
    r.gpchApi = null;
  });

  // The section can turn up late on this storefront, so watch for it — but
  // this used to run a document-wide querySelectorAll on EVERY mutation for
  // eight seconds. The theme editor mutates constantly, which made it one of
  // the most expensive things on the page. Now it coalesces to one check per
  // frame and stops the moment a carousel is found.
  if (window.MutationObserver) {
    var moQueued = false;
    var mo = new MutationObserver(function () {
      if (moQueued) return;
      moQueued = true;
      requestAnimationFrame(function () {
        moQueued = false;
        boot();
        if (document.querySelector('[data-gpch]')) mo.disconnect();
      });
    });
    mo.observe(document.body || document.documentElement, { childList: true, subtree: true });
    setTimeout(function () { mo.disconnect(); }, 8000);
  }

  window.gpcHeroCarouselLoaded = true;
})();
